import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  ResponsiveGrid,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { formatDateTime, formatTime } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  competitionCrumbs,
  lockedNotice,
  useCompetitionReadOnly,
} from "../../../shell/CompetitionLayout";
import {
  liveAccess,
  mayChangeLive,
} from "../../../lib/competitionState";
import { useCompetition } from "../../../lib/queries/competitions";
import {
  DEVICE_FUNCTION_LABELS,
  pairingQrPayload,
  stationLabel,
  useDevices,
  useRevokeDevice,
  type Device,
} from "../../../lib/queries/devices";
import { QrCode } from "../../../components/QrCode";
import type { ID } from "../../../lib/id";
import { PairingModal } from "./PairingModal";
import styles from "./DevicesPage.module.css";

// „Zařízení": the tablets and laptops of the hall, paired to a place at a
// judging table or to a function (docs/zivy-provoz-soutezi.md §7, §7b). A
// tablet belongs to the table, not to a panel: panels take turns there and
// the tablets stay. A code is made here, shown as a QR for a tablet and as
// text for a laptop, and valid for ten minutes, once. A dead tablet is
// swapped by pairing a spare to its place: the new device revokes the old.
export function DevicesPage() {
  const { id } = useParams();
  const compId = id ?? "";
  const {
    data: competition,
    isLoading,
    error,
    refetch,
  } = useCompetition(compId);
  const list = useDevices(compId);
  const revoke = useRevokeDevice(compId);
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{
    id: ID;
    code: string;
    expiresAtMs: number;
    target: string;
  } | null>(null);

  const access = liveAccess(competition);
  // A competitions_viewer sees the devices and changes nothing.
  const readOnly = useCompetitionReadOnly();
  const editable = !!competition && !readOnly && mayChangeLive(access);
  const locked = lockedNotice(competition, readOnly);
  const targetOf = (d: Pick<Device, "station" | "function">) =>
    d.station
      ? stationLabel(d.station)
      : d.function
        ? DEVICE_FUNCTION_LABELS[d.function]
        : "";

  const devices = list.data?.devices ?? [];
  // The code shown has been redeemed once it is no longer waiting (the
  // list refreshes on its own), while it is still within its time.
  const redeemed =
    !!issued &&
    !!list.data &&
    issued.expiresAtMs > Date.now() &&
    !list.data.pairings.some((p) => p.id === issued.id);

  const columns: Column<Device>[] = [
    {
      key: "label",
      header: "Zařízení",
      render: (d) => d.label || <Text variant="muted">Bez názvu</Text>,
    },
    { key: "target", header: "Pro", render: (d) => targetOf(d) },
    {
      key: "state",
      header: "Stav",
      render: (d) =>
        d.revoked_at_ms ? (
          <Badge variant="muted">Odvolané</Badge>
        ) : (
          <Badge variant="ok">Aktivní</Badge>
        ),
    },
    {
      key: "paired",
      header: "Spárováno",
      render: (d) =>
        d.paired_at_ms ? formatDateTime(dayjs(d.paired_at_ms)) : "",
    },
    {
      key: "version",
      header: "Verze aplikace",
      render: (d) => <Text variant="muted">{d.app_version}</Text>,
    },
    {
      key: "actions",
      header: "",
      render: (d) =>
        editable &&
        !d.revoked_at_ms && (
          <Table.Actions>
            <Table.Action
              onClick={() =>
                confirm({
                  title: "Odvolat zařízení",
                  description: (
                    <>
                      Odvolat <strong>{d.label || targetOf(d)}</strong>?
                      Zařízení okamžitě přestane cokoli posílat i dostávat.
                      Znovu ho lze spárovat novým kódem.
                    </>
                  ),
                  confirmLabel: "Odvolat",
                  variant: "danger",
                  onConfirm: async () => {
                    await revoke.mutateAsync(d.id);
                  },
                })
              }
            >
              Odvolat
            </Table.Action>
          </Table.Actions>
        ),
    },
  ];

  const crumbs = competitionCrumbs(compId, competition, { label: "Zařízení" });
  if (isLoading || list.isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  const failed = error ?? list.error;
  if (failed) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState
              error={failed}
              onRetry={() => {
                void refetch();
                void list.refetch();
              }}
            />
          </Page.Content>
        </Page>
      </>
    );
  }

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
          <Page.Header
            title="Zařízení"
            subtitle="Tablety a notebooky v hale: které místo u stolu nebo kterou funkci zastupují."
            actions={
              <>
                {editable && (
                  <Button
                    startIcon={<Plus aria-hidden="true" />}
                    onClick={() => setAdding(true)}
                  >
                    Přidat zařízení
                  </Button>
                )}
              </>
            }
          />
        <Page.Content stack gap="lg">
          {access === "live" && (
            <Alert variant="info">
              Soutěž probíhá: každá změna se zapisuje do živého provozu. Odvolané
              zařízení se okamžitě odpojí.
            </Alert>
          )}
          {locked && <Alert variant="info">{locked}</Alert>}

          {issued && (
            <Card>
              <Card.Header
                title="Kód pro spárování"
                subtitle={`${issued.target} · platí do ${formatTime(dayjs(issued.expiresAtMs))}, jen jednou`}
                actions={
                  <Button
                    size="sm"
                    weight="ghost"
                    onClick={() => setIssued(null)}
                  >
                    Zavřít
                  </Button>
                }
              />
              <Card.Content>
                {redeemed ? (
                  <Alert variant="ok">
                    Zařízení je spárované. Najdete ho v seznamu níže.
                  </Alert>
                ) : (
                  <ResponsiveGrid columns={2}>
                    <QrCode
                      value={pairingQrPayload(issued.code)}
                      label={`QR kód ${issued.code}`}
                      size={220}
                    />
                    <div>
                      <div className={styles.code}>
                        <Text variant="mono">{issued.code}</Text>
                      </div>
                      <p>
                        Tablet: v halové aplikaci naskenujte QR kód. Notebook: kód
                        opište. Kód se nikde neukládá; když ho zavřete, vystavte
                        nový.
                      </p>
                    </div>
                  </ResponsiveGrid>
                )}
              </Card.Content>
            </Card>
          )}

          <Page.SectionHeader
            title="Spárovaná zařízení"
            meta={
              (list.data?.pairings.length ?? 0) > 0
                ? `Nevyzvednuté kódy: ${list.data?.pairings.length}`
                : undefined
            }
          />
          <Table<Device>
            framed
            rows={devices}
            rowKey={(d) => d.id}
            columns={columns}
            empty={
              <EmptyState
                title="Zatím žádné zařízení"
                description="Přidejte zařízení a v hale ho spárujte kódem."
              />
            }
          />
        </Page.Content>
      </Page>

      <PairingModal
        competitionId={compId}
        tables={list.data?.tables ?? []}
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={setIssued}
      />
    </>
  );
}
